import React, { useEffect, useState } from "react";
import axios from "axios";
import { Medal, Flame, Trophy, Info, Award, ChevronUp, ChevronDown, Minus, MapPin,
  Shirt, Shield, GraduationCap, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MEDAL_COLORS = { 1: "#F5C518", 2: "#C9CBD1", 3: "#CD7F32" };
const badgeColor = (b) => (b >= 100 ? "#F5C518" : b >= 30 ? "#C9CBD1" : "#CD7F32");
const streakBadge = (r) => r.badge ?? (r.streak >= 100 ? 100 : r.streak >= 30 ? 30 : r.streak >= 7 ? 7 : 0);
const avatarSrc = (r) => (r.avatar_url ? `${BACKEND}${r.avatar_url}` : null);

// ---- Season countdown to the Sept 30 jersey giveaway ----
function nextSep30() {
  const now = new Date();
  let target = new Date(now.getFullYear(), 8, 30, 23, 59, 59); // month 8 = September
  if (target < now) target = new Date(now.getFullYear() + 1, 8, 30, 23, 59, 59);
  return target;
}
const CountdownTimer = () => {
  const calc = () => {
    const ms = Math.max(0, nextSep30().getTime() - Date.now());
    return { d: Math.floor(ms / 86400000), h: Math.floor(ms / 3600000) % 24, m: Math.floor(ms / 60000) % 60, s: Math.floor(ms / 1000) % 60 };
  };
  const [t, setT] = useState(calc);
  useEffect(() => { const id = setInterval(() => setT(calc()), 1000); return () => clearInterval(id); }, []);
  const cell = (v, l) => (
    <div className="flex flex-col items-center">
      <span className="font-cover text-2xl sm:text-3xl leading-none text-white tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] uppercase tracking-widest text-white/45 mt-1">{l}</span>
    </div>
  );
  return (
    <div data-testid="lb-countdown" className="flex items-center gap-3 sm:gap-4">
      {cell(t.d, "Days")}<span className="text-white/25 text-xl -mt-2">:</span>
      {cell(t.h, "Hrs")}<span className="text-white/25 text-xl -mt-2">:</span>
      {cell(t.m, "Min")}<span className="text-white/25 text-xl -mt-2">:</span>
      {cell(t.s, "Sec")}
    </div>
  );
};

const GoalBanner = ({ accent }) => (
  <div data-testid="lb-goal-banner" className="relative overflow-hidden rounded-xl border p-5 mb-4"
    style={{ borderColor: `${accent}80`, background: `linear-gradient(135deg, ${accent}4d, #1a0708, #0d0d0d)` }}>
    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-2xl" style={{ background: `${accent}40` }} />
    <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-[#F5C518]/15 flex items-center justify-center shrink-0"><Shirt size={22} className="text-[#F5C518]" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5C518] font-bold mb-1">The Goal 🏆</div>
          <p className="font-headings text-lg sm:text-xl uppercase tracking-wide leading-tight text-white">Top of the board on Sept 30th wins a free <span style={{ color: accent }}>Next O Training Jersey!</span></p>
        </div>
      </div>
      <div className="shrink-0 sm:border-l sm:border-white/10 sm:pl-5">
        <CountdownTimer />
      </div>
    </div>
  </div>
);

const Avatar = ({ r, size = 32, accent = "#C41E24" }) => (
  <div className="rounded-full overflow-hidden font-bold flex items-center justify-center flex-shrink-0"
    style={{ width: size, height: size, fontSize: size * 0.4, color: accent, backgroundColor: `${accent}26` }}>
    {avatarSrc(r) ? <img loading="lazy" decoding="async" src={avatarSrc(r)} alt="" className="w-full h-full object-cover" /> : (r.name || "?")[0].toUpperCase()}
  </div>
);

const Podium = ({ rows, accent = "#C41E24" }) => {
  if (!rows || rows.length < 3) return null;
  const [first, second, third] = rows;
  const spot = (r, place) => {
    const big = place === 1;
    return (
      <div data-testid={`lb-podium-${place}`} className={`flex flex-col items-center ${big ? "-mt-4" : "mt-4"}`}>
        <div className="relative">
          {big && <Crown size={20} className="absolute -top-5 left-1/2 -translate-x-1/2 text-[#F5C518]" />}
          <div className="rounded-full p-[3px]" style={{ background: MEDAL_COLORS[place] }}>
            <Avatar r={r} size={big ? 76 : 60} accent={accent} />
          </div>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-cover text-black" style={{ background: MEDAL_COLORS[place] }}>{place}</div>
        </div>
        <span className="mt-3 text-sm font-bold truncate max-w-[110px] text-center">{r.name}</span>
        <span className="text-xs font-bold" style={{ color: accent }}>{(r.points || 0).toLocaleString()} pts</span>
        {r.club && <span className="text-[10px] text-white/40 truncate max-w-[110px] text-center">{r.club}</span>}
      </div>
    );
  };
  return (
    <div data-testid="lb-podium" className="flex items-end justify-center gap-6 sm:gap-10 bg-[#0d0d0d] border border-white/10 rounded-xl px-4 pt-8 pb-5 mb-3">
      {spot(second, 2)}{spot(first, 1)}{spot(third, 3)}
    </div>
  );
};

const StatCard = ({ title, icon: Icon, items, testid, accent = "#C41E24" }) => (
  <div data-testid={testid} className="bg-[#141414] border border-white/10 rounded-lg p-3.5">
    <div className="flex items-center gap-1.5 mb-2.5"><Icon size={14} style={{ color: accent }} /><span className="text-[11px] font-bold uppercase tracking-wider text-white/70">{title}</span></div>
    <div className="space-y-1.5">
      {items.slice(0, 3).map((g, i) => (
        <div key={g.name} className="flex items-center gap-2 text-xs">
          <span className="w-4 font-bold text-white/40">{i + 1}</span>
          <span className="flex-1 truncate text-white/85">{g.name} <span className="text-white/30">· {g.players}</span></span>
          <span className="font-bold" style={{ color: accent }}>{(g.points || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  </div>
);

const RegionalStats = ({ clubs, schools, accent = "#C41E24" }) => {
  if (!clubs?.length && !schools?.length) return null;
  return (
    <div data-testid="lb-regional-stats" className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {!!clubs?.length && <StatCard title="Top Clubs" icon={Shield} items={clubs} testid="lb-top-clubs" accent={accent} />}
      {!!schools?.length && <StatCard title="Top Schools" icon={GraduationCap} items={schools} testid="lb-top-schools" accent={accent} />}
    </div>
  );
};

const RankMovement = ({ r }) => {
  const m = r.movement;
  if (m === undefined || m === null)
    return <span data-testid={`lb-move-new-${r.rank}`} className="text-[9px] font-bold uppercase tracking-wide text-[#F5C518]/90" title="New this week">NEW</span>;
  if (m > 0)
    return <span data-testid={`lb-move-up-${r.rank}`} className="flex items-center gap-0.5 text-[10px] font-bold text-[#22C55E]" title={`Up ${m} this week`}><ChevronUp size={13} strokeWidth={3} />{m}</span>;
  if (m < 0)
    return <span data-testid={`lb-move-down-${r.rank}`} className="flex items-center gap-0.5 text-[10px] font-bold text-[#C41E24]" title={`Down ${Math.abs(m)} this week`}><ChevronDown size={13} strokeWidth={3} />{Math.abs(m)}</span>;
  return <span data-testid={`lb-move-same-${r.rank}`} className="flex items-center text-white/30" title="No change this week"><Minus size={12} strokeWidth={3} /></span>;
};

const SAMPLE = [
  { rank: 1, name: "Marcus R.", points: 2450, streak: 21, movement: 1, club: "Central IL FC", school: "Normal West HS" },
  { rank: 2, name: "Sofia L.", points: 2310, streak: 18, movement: -1, club: "Bloomington United", school: "Central Catholic HS" },
  { rank: 3, name: "Diego M.", points: 2180, streak: 14, movement: 2, club: "Central IL FC", school: "Normal West HS" },
  { rank: 4, name: "Ava T.", points: 1990, streak: 9, movement: 0, club: "Peoria Elite", school: "Richwoods HS" },
  { rank: 5, name: "Liam K.", points: 1840, streak: 7, movement: null, club: "Bloomington United", school: "Central Catholic HS" },
];
const SAMPLE_CLUBS = [{ name: "Central IL FC", points: 4630, players: 2 }, { name: "Bloomington United", points: 4150, players: 2 }, { name: "Peoria Elite", points: 1990, players: 1 }];
const SAMPLE_SCHOOLS = [{ name: "Normal West HS", points: 4630, players: 2 }, { name: "Central Catholic HS", points: 4150, players: 2 }, { name: "Richwoods HS", points: 1990, players: 1 }];

const Row = ({ r, sample, showMovement = true, accent = "#C41E24" }) => (
  <div data-testid={sample ? undefined : `lb-row-${r.rank}`}
    className="flex items-center gap-3 rounded-md px-2 py-1.5"
    style={r.is_you ? { backgroundColor: `${accent}1f`, border: `1px solid ${accent}66` } : undefined}>
    <div className="w-7 flex flex-col items-center justify-center flex-shrink-0 gap-0.5">
      {r.rank <= 3 ? <Medal size={20} style={{ color: MEDAL_COLORS[r.rank] }} /> : <span className="text-sm font-bold text-white/40">{r.rank}</span>}
      {showMovement && <RankMovement r={r} />}
    </div>
    <Avatar r={r} size={32} accent={accent} />
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-sm font-semibold truncate flex items-center">{r.name}{r.is_elite && <Crown size={12} className="ml-1 text-[#D4AF37] shrink-0" title="Elite member" />}{r.is_you && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider" style={{ color: accent }}>You</span>}</span>
        <span className="text-xs font-bold" style={{ color: accent }}>{(r.points || 0).toLocaleString()} pts</span>
      </div>
      {(r.club || r.school) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          {r.club && (
            <span data-testid={`lb-club-${r.rank}`} className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-bold uppercase tracking-wide rounded px-1.5 sm:px-2 py-0.5 sm:py-1 max-w-full truncate border"
              style={{ color: "#fff", backgroundColor: `${accent}33`, borderColor: `${accent}99`, boxShadow: `0 0 10px ${accent}44` }}>
              <Shield size={12} className="shrink-0" style={{ color: accent }} /> {r.club}
            </span>
          )}
          {r.school && (
            <span data-testid={`lb-school-${r.rank}`} className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-bold uppercase tracking-wide rounded px-1.5 sm:px-2 py-0.5 sm:py-1 max-w-full truncate border"
              style={{ color: "#fff", backgroundColor: "rgba(212,175,55,0.16)", borderColor: "rgba(212,175,55,0.6)", boxShadow: "0 0 10px rgba(212,175,55,0.25)" }}>
              <GraduationCap size={12} className="shrink-0" style={{ color: "#D4AF37" }} /> {r.school}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 text-[10px] text-white/40 flex-wrap">
        {r.streak > 0 && <span className="flex items-center gap-0.5"><Flame size={10} style={{ color: accent }} /> {r.streak}-day streak</span>}
        {streakBadge(r) > 0 && (
          <span data-testid={`lb-badge-${streakBadge(r)}`} className="flex items-center gap-0.5 font-bold px-1.5 py-0.5 rounded-full"
            style={{ color: badgeColor(streakBadge(r)), backgroundColor: `${badgeColor(streakBadge(r))}22` }}>
            <Award size={10} /> {streakBadge(r)}-Day Badge
          </span>
        )}
      </div>
    </div>
  </div>
);

export default function Leaderboard({ token, sampleFallback = false, limit = 10, isElite = false }) {
  const [data, setData] = useState(null);
  const [showScoring, setShowScoring] = useState(false);
  const scope = "local"; // Central Illinois is the one and only board
  const accent = isElite ? "#D4AF37" : "#C41E24";

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    setData(null);
    axios.get(`${API}/leaderboard?limit=${limit}&scope=${scope}`, { headers })
      .then(({ data }) => setData(data))
      .catch(() => setData({ top: [], you: null, total_players: 0 }));
  }, [token, limit, scope]);

  const anyPoints = data?.top?.some((r) => r.points > 0);
  const useSample = sampleFallback && !anyPoints;
  const hasReal = !useSample && data && data.top && data.top.length > 0;
  const rows = useSample ? SAMPLE : (hasReal ? data.top : []);
  const clubs = useSample ? SAMPLE_CLUBS : (data?.top_clubs || []);
  const schools = useSample ? SAMPLE_SCHOOLS : (data?.top_schools || []);
  const you = data?.you;
  const inTop = you && rows.some((r) => r.is_you);

  return (
    <div data-testid="leaderboard">
      <GoalBanner accent={accent} />

      <div data-testid="leaderboard-header" className="flex items-center gap-3 mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "#D4AF3755", background: "linear-gradient(120deg, rgba(212,175,55,0.10), rgba(13,13,13,0.9))" }}>
        <MapPin size={16} className="shrink-0" style={{ color: "#D4AF37" }} />
        <div className="min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/45">Central Illinois Leaderboard</div>
          <h3 data-testid="leaderboard-title" className="font-headings text-base sm:text-lg uppercase tracking-wide leading-tight" style={{ color: "#D4AF37" }}>The Official Record of Central Illinois Technical Excellence</h3>
        </div>
      </div>

      {rows.length >= 3 && <Podium rows={rows} accent={accent} />}

      <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-4 space-y-1.5">
        {rows.length === 0 && (
          <p className="text-sm text-white/40 py-4 text-center">
            {scope === "local"
              ? "No Central Illinois players ranked yet — set your Location in Profile to claim a local spot!"
              : "No rankings yet — upload a drill or log a session to get on the board!"}
          </p>
        )}
        {rows.map((r, i) => <Row key={r.rank || i} r={r} sample={!hasReal} showMovement={false} accent={accent} />)}

        {you && !inTop && (
          <>
            <div className="text-center text-white/25 text-xs py-1">···</div>
            <Row r={you} showMovement={false} accent={accent} />
          </>
        )}
      </div>

      <RegionalStats clubs={clubs} schools={schools} accent={accent} />

      {hasReal && (
        <button data-testid="lb-scoring-toggle" onClick={() => setShowScoring((s) => !s)}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white transition-colors">
          <Info size={12} /> How points work
        </button>
      )}
      {showScoring && data?.scoring && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-white/55 bg-[#141414] border border-white/10 rounded-md p-3">
          <span><b className="text-white">+{data.scoring.chapter}</b> per chapter completed</span>
          <span><b className="text-white">+{data.scoring.drill_upload}</b> per drill video</span>
          <span><b className="text-white">+{data.scoring.coach_approved_bonus}</b> coach-approved bonus</span>
          <span><b className="text-white">+{data.scoring.journal}</b> per training log</span>
          <span><b className="text-white">+{data.scoring.streak_day}</b> per day of your streak</span>
          <span className="col-span-2 pt-1 border-t border-white/10 flex items-center gap-1.5"><Award size={12} className="text-[#F5C518]" /> Streak badges at <b className="text-white">7</b>, <b className="text-white">30</b> &amp; <b className="text-white">100</b> days</span>
        </div>
      )}
      {!hasReal && sampleFallback && (
        <p className="text-xs text-white/40 mt-4 flex items-center gap-1.5"><Trophy size={12} className="text-[#C41E24]" /> Sample ranking — go live to claim your spot</p>
      )}
    </div>
  );
}
